import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { Hero } from '../components/Hero';
import { Outcomes } from '../components/Outcomes';
import { Leak } from '../components/Leak';
import { System } from '../components/System';
import { CTAButton } from '../components/CTAButton';
import { Bloom } from '../components/Bloom';

/* Closing section doubles as the homepage's navigation menu. The header nav
   is behind a hamburger on mobile, so someone who scrolls the whole page had
   no in-page route to pricing or why. Three link cards (not three equal
   buttons) so the primary Book a call CTA still wins the hierarchy, and each
   card answers the question a visitor actually has instead of repeating the
   nav label. */
const routes = [{
  to: '/how-it-works',
  label: 'How it works',
  body: 'Every piece that gets set up, and what each one does for you.'
}, {
  to: '/pricing',
  label: 'Pricing',
  body: 'What it costs, right on the page. You do not have to book a call to find out.'
}, {
  to: '/why',
  label: 'Why Viridia',
  body: 'What makes this different from the last agency that pitched you.'
}];
export function Home() {
  return <>
      <SEOHead title="AI Receptionist + CRM for Phoenix Home-Service Businesses | Viridia Analytics" description="One system that catches every call, text, and Yelp lead for Phoenix contractors. AI receptionist, CRM, and follow-up that runs on its own. Month to month." canonical="/" jsonLd={LOCAL_BUSINESS_SCHEMA} data-id="element-2376" data-mp="2mwqql7nfr238" />
      <Hero data-id="element-2377" data-mp="239soxvqbu7cw" />
      <Outcomes data-id="element-2378" data-mp="23a2g3mn3ykk6" />
      <Leak data-id="element-2379" data-mp="23am1xefvpt3h" />
      <System data-id="element-2380" data-mp="236y4w5b86ok0" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2381" data-mp="2376lo8agye0h">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-2382" data-mp="1z9ihsfqstawg" />
        <div className="mx-auto w-full max-w-5xl px-6 py-24 lg:py-28" data-id="element-2383" data-mp="1u8uuo55oxe7i">
          <div className="text-center" data-id="element-2384" data-mp="1kkh48btwt7ia">
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2385" data-mp="1kmtncsbelnwx">
              That&rsquo;s the short version.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-2386" data-mp="1ankvc503mjd7">
              Pick whichever one you came here for.
            </p>
          </div>

          <nav aria-label="Where to next" className="mt-12" data-id="element-2387" data-mp="23aou3s8lm1gj">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-2388" data-mp="23dbbejlajvby">
              {routes.map((r, i) => <motion.li key={r.to} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} data-id="element-2389" data-mp="28c4gpoqfcg11" data-mpi={i}>
                  <Link to={r.to} className="group flex h-full flex-col rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] p-6 transition-colors hover:border-emerald-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]" data-id="element-2390" data-mp="2m89mh9akni5w">
                    <span className="flex items-center gap-2 text-base font-semibold text-white" data-id="element-2391" data-mp="odmar9e4t5ka">
                      {r.label}
                      <ArrowRightIcon className="h-4 w-4 text-emerald-500 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2392" data-mp="kewp6tixwhpk" />
                    </span>
                    <span className="mt-2.5 text-sm leading-relaxed text-neutral-400" data-id="element-2393" data-mp="5opdcwnftwxu">
                      {r.body}
                    </span>
                  </Link>
                </motion.li>)}
            </ul>
          </nav>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4" data-id="element-2394" data-mp="1h4w5ja5c0kip">
            <CTAButton to="/contact" data-id="element-2395" data-mp="1c3gk3gz8sbzs">Book a 15-min call</CTAButton>
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600" data-id="element-2396" data-mp="27eodnrlw2wkg">
              Month to month &middot; No contracts
            </span>
          </div>
        </div>
      </section>
    </>;
}